import {
    useState,
    useRef,
    useCallback,
    useEffect,
    useMemo
} from 'react';
import {useSelector} from 'react-redux';
import {selectConfig} from '@components/appSlice';
import {signingApi} from '../../api/signing/api';
import {
    DEVELOPMENT_ENV,
    DEV_ENV
} from '@constants/main';
import {TOKEN_READER_CONNECTION_FAILED_MESSAGE} from '@components/SignerUi/HardwareTokenSigning/constants';

const WEBSOCKET_PORTS = [8086, 26666, 31222, 32444, 44555, 47777, 48888] as const;
const HARDCODED_PIN = '1234';

interface HardwareTokenCertificate {
    alias: string;
    serial: string;
    issuer: string;
    subject: string;
    validFrom: string;
    validTo: string;
    certificate: string;
    commonName?: string;
    hasPrivateKey?: boolean;
    algorithm?: string;
}

interface TokenStatusMessage {
    type: 'tokenStatus';
    available: boolean;
    certificates?: HardwareTokenCertificate[];
    count?: number;
    message?: string;
}

interface SignResultMessage {
    type: 'signResult';
    signedHash: string;
    certificate: {
        alias: string;
        serial: string;
    };
}

interface ErrorMessage {
    type: string;
    error: string;
    code: string;
}

interface VersionMessage {
    version: string;
}

type HardwareTokenServerMessage =
    | VersionMessage
    | TokenStatusMessage
    | SignResultMessage
    | ErrorMessage
    | {type: 'pong'; timestamp: number};

interface UseHardwareTokenOptions {
    onSuccess?: () => void;
    onError?: (error: string) => void;
    accessToken?: string;
}

interface UseHardwareTokenResult {
    isConnected: boolean;
    isConnecting: boolean;
    certificates: HardwareTokenCertificate[];
    selectedCertificate: HardwareTokenCertificate | null;
    setSelectedCertificate: (certificate: HardwareTokenCertificate | null) => void;
    isSigning: boolean;
    error: string | null;
    connect: () => void;
    disconnect: () => void;
    handleSign: (transactionId: string, documentId: string, recipientId: string, certificate: HardwareTokenCertificate) => Promise<boolean>;
}

function getIsVersionMessage(message: unknown): message is VersionMessage {
    return typeof message === 'object'
        && message !== null
        && 'version' in message
        && typeof (message as VersionMessage).version === 'string';
}

function getIsTokenStatusMessage(message: unknown): message is TokenStatusMessage {
    return typeof message === 'object'
        && message !== null
        && 'type' in message
        && (message as TokenStatusMessage).type === 'tokenStatus';
}

function getIsSignResultMessage(message: unknown): message is SignResultMessage {
    return typeof message === 'object'
        && message !== null
        && 'type' in message
        && (message as SignResultMessage).type === 'signResult';
}

function getIsErrorMessage(message: unknown): message is ErrorMessage {
    return typeof message === 'object'
        && message !== null
        && 'error' in message
        && typeof (message as ErrorMessage).error === 'string';
}

function getWebSocketUrl(port: number): string {
    return `ws://localhost:${port}/`;
}

function getIsPinRequiredEnvironment(environment: string): boolean {
    return environment === DEVELOPMENT_ENV
        || environment === DEV_ENV;
}

function getErrorMessageByCode(code: string): string {
    const errorMessages: Record<string, string> = {
        '100': TOKEN_READER_CONNECTION_FAILED_MESSAGE,
        '103': 'Invalid PIN code',
        '104': 'Insert hardware token',
        '105': 'Protocol error',
        '108': 'Request error'
    };
    return errorMessages[code] || 'Unknown error';
}

/**
 * Hook for managing Hardware Token signing flow.
 * Handles WebSocket connection to Token Reader desktop application,
 * certificate selection, and document signing with hardware tokens.
 *
 * @param options - Configuration options
 * @param options.onSuccess - Callback invoked when document is signed successfully
 * @param options.onError - Callback invoked when an error occurs
 * @param options.accessToken - Access token for API authentication
 * @returns Hardware token state and control functions
 */
export function useHardwareToken({
    onSuccess,
    onError,
    accessToken
}: UseHardwareTokenOptions = {}): UseHardwareTokenResult {
    const config = useSelector(selectConfig);
    const environment = (config?.VS_ENV as string) || DEVELOPMENT_ENV;

    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [certificates, setCertificates] = useState<HardwareTokenCertificate[]>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<HardwareTokenCertificate | null>(null);
    const [isSigning, setIsSigning] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const webSocketRef = useRef<WebSocket | null>(null);
    const signingResolverRef = useRef<{
        resolve: (signedHash: string) => void;
        reject: (error: Error) => void;
    } | null>(null);
    const currentPortIndexRef = useRef(0);

    const isPinRequired = useMemo(
        () => getIsPinRequiredEnvironment(environment),
        [environment]
    );

    const handleWebSocketMessage = useCallback((event: MessageEvent) => {
        try {
            const message = JSON.parse(event.data) as HardwareTokenServerMessage;

            if (getIsVersionMessage(message)) {
                return;
            }

            if (getIsTokenStatusMessage(message)) {
                if (message.available && message.certificates) {
                    setCertificates(message.certificates);
                    setError(null);
                } else {
                    setCertificates([]);
                    setError(message.message || 'Insert hardware token');
                }
                return;
            }

            if (getIsSignResultMessage(message)) {
                if (signingResolverRef.current) {
                    signingResolverRef.current.resolve(message.signedHash);
                    signingResolverRef.current = null;
                }
                return;
            }

            if (getIsErrorMessage(message)) {
                const errorMessage = getErrorMessageByCode(message.code);
                setError(errorMessage);
                onError?.(errorMessage);

                if (signingResolverRef.current) {
                    signingResolverRef.current.reject(new Error(errorMessage));
                    signingResolverRef.current = null;
                }
            }
        } catch {
            setError('Protocol error');
        }
    }, [onError]);

    const handleWebSocketOpen = useCallback(() => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
    }, []);

    const handleWebSocketClose = useCallback(() => {
        setIsConnected(false);
        setIsConnecting(false);
        setCertificates([]);
        setSelectedCertificate(null);

        if (signingResolverRef.current) {
            signingResolverRef.current.reject(new Error('Connection closed'));
            signingResolverRef.current = null;
        }
    }, []);

    const tryConnectToPort = useCallback((portIndex: number): void => {
        if (portIndex >= WEBSOCKET_PORTS.length) {
            const errorMessage = TOKEN_READER_CONNECTION_FAILED_MESSAGE;
            setError(errorMessage);
            setIsConnecting(false);
            onError?.(errorMessage);
            return;
        }

        const port = WEBSOCKET_PORTS[portIndex];
        const url = getWebSocketUrl(port);

        try {
            const webSocket = new WebSocket(url);

            webSocket.onopen = () => {
                currentPortIndexRef.current = portIndex;
                handleWebSocketOpen();
            };

            webSocket.onmessage = handleWebSocketMessage;
            webSocket.onclose = handleWebSocketClose;

            webSocket.onerror = () => {
                webSocket.onopen = null;
                webSocket.onmessage = null;
                webSocket.onclose = null;
                webSocket.onerror = null;

                try {
                    webSocket.close();
                } catch {
                    // ignore
                }

                tryConnectToPort(portIndex + 1);
            };

            webSocketRef.current = webSocket;
        } catch {
            tryConnectToPort(portIndex + 1);
        }
    }, [handleWebSocketOpen, handleWebSocketMessage, handleWebSocketClose, onError]);

    const connect = useCallback(() => {
        if (webSocketRef.current) {
            return;
        }

        setIsConnecting(true);
        setError(null);
        currentPortIndexRef.current = 0;

        tryConnectToPort(0);
    }, [tryConnectToPort]);

    const disconnect = useCallback(() => {
        if (webSocketRef.current) {
            webSocketRef.current.close();
            webSocketRef.current = null;
        }
        setIsConnected(false);
        setIsConnecting(false);
        setCertificates([]);
        setSelectedCertificate(null);
        setIsSigning(false);
        setError(null);
    }, []);

    const sendSignHashRequest = useCallback((
        certificate: HardwareTokenCertificate,
        hashToSign: string
    ): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (!webSocketRef.current || webSocketRef.current.readyState !== WebSocket.OPEN) {
                reject(new Error('WebSocket not connected'));
                return;
            }

            signingResolverRef.current = {resolve, reject};

            const message = {
                type: 'signHash',
                cert: {
                    alias: certificate.alias,
                    serial: certificate.serial,
                    issuer: certificate.issuer,
                    subject: certificate.subject
                },
                hash: hashToSign,
                ...(isPinRequired && {pin: HARDCODED_PIN})
            };

            webSocketRef.current.send(JSON.stringify(message));
        });
    }, [isPinRequired]);

    const handleSign = useCallback(async (
        transactionId: string,
        documentId: string,
        recipientId: string,
        certificate: HardwareTokenCertificate
    ): Promise<boolean> => {
        if (!certificate) {
            const errorMessage = 'No certificate selected';
            setError(errorMessage);
            onError?.(errorMessage);
            return false;
        }

        if (!isConnected) {
            const errorMessage = 'Not connected to Token Reader';
            setError(errorMessage);
            onError?.(errorMessage);
            return false;
        }

        setIsSigning(true);
        setError(null);

        try {
            const authHeaders = accessToken
                ? {Authorization: `Bearer ${accessToken}`}
                : undefined;

            const prepareResponse = await signingApi.prepareForSigning({
                transactionId,
                documentId,
                recipientId,
                certificate: certificate.certificate,
                options: {headers: authHeaders}
            });

            const {session_id, hashToSignBase64} = prepareResponse.data;
            const signedHash = await sendSignHashRequest(certificate, hashToSignBase64);

            await signingApi.confirmDocument({
                transactionId,
                documentId,
                recipientId,
                data: {
                    session_id,
                    signature: signedHash,
                    certificate: certificate.certificate
                },
                options: {headers: authHeaders}
            });

            setIsSigning(false);
            onSuccess?.();
            return true;
        } catch (err) {
            setIsSigning(false);
            const errorMessage = err instanceof Error
                ? err.message
                : 'Signing failed';
            setError(errorMessage);
            onError?.(errorMessage);
            return false;
        }
    }, [
        isConnected,
        accessToken,
        sendSignHashRequest,
        onSuccess,
        onError
    ]);

    useEffect(() => {
        return () => {
            if (webSocketRef.current) {
                webSocketRef.current.close();
                webSocketRef.current = null;
            }
        };
    }, []);

    return {
        isConnected,
        isConnecting,
        certificates,
        selectedCertificate,
        setSelectedCertificate,
        isSigning,
        error,
        connect,
        disconnect,
        handleSign
    };
}

export type {
    HardwareTokenCertificate,
    UseHardwareTokenOptions,
    UseHardwareTokenResult
};

export default useHardwareToken;
